import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ShieldCheck, FileText, Cookie, Database, Download, Settings, Trash2, FileSignature } from "lucide-react";

const DOCS = [
  { slug: "privacy-policy", title: "Privacy Policy", desc: "How EthicCred collects, uses, and protects information.", icon: ShieldCheck },
  { slug: "cookie-policy", title: "Cookie Policy", desc: "Cookies and tracking technologies used by the Platform.", icon: Cookie },
  { slug: "data-retention-policy", title: "Data Retention Policy", desc: "How long we keep each category of information.", icon: Database },
  { slug: "consent-verification-policy", title: "Consent & Verification Policy", desc: "What you authorize when records are verified.", icon: FileSignature },
];

const ACTIONS = [
  { slug: "manage-consent", title: "Manage Consent", desc: "Review and update which authorizations you've granted.", icon: Settings },
  { slug: "download-data", title: "Download My Data", desc: "Export a copy of your professional passport and records.", icon: Download },
  { slug: "account-closure", title: "Request Account Closure", desc: "Close your account. Audit-relevant records may be retained per the Data Retention Policy.", icon: Trash2 },
];

export default function PrivacyCenter() {
  return (
    <div className="min-h-screen bg-ec-bg" data-testid="privacy-center-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-14">
        <div className="text-center max-w-3xl mx-auto">
          <div className="eyebrow">Privacy Center</div>
          <h1 className="font-display text-5xl sm:text-6xl font-black tracking-tighter mt-3">
            Your data. Your controls.
          </h1>
          <p className="text-ec-ink-soft mt-5 leading-relaxed">
            EthicCred is built on the principle that your professional records belong to you.
            Review our policies, manage your consents, and exercise your data rights from one
            place.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mt-16">
          {DOCS.map((d) => (
            <Link to={`/legal/${d.slug}`} key={d.slug} data-testid={`privacy-doc-${d.slug}`}>
              <Card className="rounded-sm border-ec-line hover:-translate-y-0.5 transition-transform">
                <CardContent className="p-6 flex items-start gap-4">
                  <d.icon className="w-6 h-6 text-ec-emerald mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-display font-bold text-lg">{d.title}</div>
                    <div className="text-sm text-ec-ink-soft mt-1">{d.desc}</div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        <div className="mt-12 border-t border-ec-line pt-12">
          <h2 className="font-display text-2xl font-black tracking-tight">Privacy actions</h2>
          <p className="text-ec-ink-soft text-sm mt-2">
            These actions are coming soon. In the interim, contact us at <span className="font-mono-plex">privacy@ethiccred.com</span> to exercise your rights.
          </p>
          <div className="grid md:grid-cols-3 gap-4 mt-6">
            {ACTIONS.map((a) => (
              <Card key={a.slug} className="rounded-sm border-ec-line">
                <CardContent className="p-6">
                  <a.icon className="w-5 h-5 text-ec-ink" />
                  <div className="font-display font-bold text-base mt-3">{a.title}</div>
                  <div className="text-sm text-ec-ink-soft mt-1">{a.desc}</div>
                  <Button variant="outline" className="rounded-sm border-ec-ink mt-4 w-full" disabled data-testid={`privacy-action-${a.slug}`}>
                    Coming soon
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
